import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OfficerService } from '@app/modules/officer/services/officer.service';
import {
  OfficerCompanyType,
  OfficerFiltersInput,
  OfficerPersonType,
  OfficerType,
  OfficerTypeEnum,
  SupportedCountryType,
} from '@generated/graphql';
import { isNil, omitBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import {
  LookupCorporateFormType,
  LookupIndividualFormType,
} from './lookup-form-type';
import { finalize } from 'rxjs/operators';

export class LookupOfficerDialogState {
  private readonly formBuilder = new FormBuilder();

  selectedOfficerType: OfficerTypeEnum;
  searchForm:
    | FormGroup<LookupIndividualFormType>
    | FormGroup<LookupCorporateFormType>;

  get individualForm() {
    return this.searchForm as FormGroup<LookupIndividualFormType>;
  }

  get corporateForm() {
    return this.searchForm as FormGroup<LookupCorporateFormType>;
  }

  officerService: OfficerService;

  results: OfficerType[] = [];

  get individualResults() {
    if (this.results.length === 0 || !('firstName' in this.results[0].officerProfile)) {
      return [];
    }

    return this.results.map((officer) => officer.officerProfile as OfficerPersonType);
  }

  get corporateResults() {
    if (
      this.results.length === 0 ||
      !('registrationNumber' in this.results[0].officerProfile)
    ) {
      return [];
    }

    return this.results.map((officer) => officer.officerProfile as OfficerCompanyType);
  }

  loading$ = new BehaviorSubject(false);

  countryOfIncorporation: SupportedCountryType;

  excludedOfficerIds: string[] = [];

  constructor() {
    this.selectedOfficerType = OfficerTypeEnum.Individual;

    this.rebuildForm();
  }

  setOfficerType(officerType: OfficerTypeEnum): void {
    this.selectedOfficerType = officerType;

    this.rebuildForm();
  }

  rebuildForm(): void {
    if (this.isCorporate()) {
      this.buildCorporateForm();
    } else {
      this.buildIndividualForm();
    }
  }

  private buildIndividualForm(): void {
    this.searchForm = this.formBuilder.group<LookupIndividualFormType>({
      firstName: this.formBuilder.control(null, [Validators.required]),
      lastName: this.formBuilder.control(null, [Validators.required]),
      identificationTypeId: this.formBuilder.control(null, [Validators.required]),
      identificationNumber: this.formBuilder.control(null, [Validators.required]),
    });
  }

  private buildCorporateForm(): void {
    this.searchForm = this.formBuilder.group<LookupCorporateFormType>({
      name: this.formBuilder.control(null, [Validators.required]),
      registrationNumber: this.formBuilder.control(null, [Validators.required]),
    });
  }

  isIndividual(): boolean {
    return this.selectedOfficerType === OfficerTypeEnum.Individual;
  }

  isCorporate(): boolean {
    return this.selectedOfficerType === OfficerTypeEnum.Corporate;
  }

  lookupOfficers(): void {
    this.results = [];
    this.loading$.next(true);

    this.officerService
      .lookupOfficersForExpert(this.buildFilters())
      .pipe(finalize(() => this.loading$.next(false)))
      .subscribe({
      next: (result) => {
        this.results = result;
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  getOfficerResult(index: number) {
    return this.results.find((officer, officerIndex) => officerIndex === index);
  }

  private buildFilters(): OfficerFiltersInput {
    const filters = {
      ...this.searchForm.getRawValue(),
      type: this.selectedOfficerType,
      exceptOfficerIds:
        this.excludedOfficerIds.length > 0 ? this.excludedOfficerIds : undefined,
    };

    return omitBy(filters, isNil) as OfficerFiltersInput;
  }

  // INFO: keep this function for later if needed

  // filterResult(data: OfficerType[]): OfficerType[] {
  //   let result: OfficerType[] = [];
  //
  //   if (this.countryOfIncorporation.country.code.toUpperCase() === 'SG') {
  //     result = data.filter((t) => {
  //       if ('firstName' in t.officerProfile) {
  //         return (
  //           t.officerProfile.identification.officerIdentificationType.name.toUpperCase() !==
  //           'ID'
  //         );
  //       } else {
  //         return (
  //           (
  //             t.officerProfile.representative
  //               .officerProfile as OfficerPersonType
  //           ).identification.officerIdentificationType.name.toUpperCase() !==
  //           'ID'
  //         );
  //       }
  //     });
  //   } else {
  //     result = data.filter((t) => {
  //       if ('firstName' in t.officerProfile) {
  //         return OTHER_IDENTIFICATION_TYPE_FILTER.includes(
  //           t.officerProfile.identification.officerIdentificationType.name.toUpperCase(),
  //         );
  //       } else {
  //         return OTHER_IDENTIFICATION_TYPE_FILTER.includes(
  //           (
  //             t.officerProfile.representative
  //               .officerProfile as OfficerPersonType
  //           ).identification.officerIdentificationType.name.toUpperCase(),
  //         );
  //       }
  //     });
  //   }
  //
  //   return result;
  // }
}
